/**
 * quoteI18nExt.js — additive translations for the enhanced public quote form.
 * Everything is FR/DE/EN — the site is trilingual.
 * Loaded alongside the main extraI18n dictionary.
 */
const IDK_FR = "Je ne sais pas";
const IDK_DE = "Ich weiss nicht";
const IDK_EN = "I don't know";

const FR = {
  intro: {
    banner: "Expliquez-nous votre besoin en quelques clics. Pas besoin d'être précis à 100 %, nous vous rappellerons pour clarifier les détails et préparer votre devis.",
    duration: "≈ 1 minute",
    freeNoEng: "Gratuit et sans engagement",
  },
  steps: {
    service: "Service",
    details: "Détails",
    contact: "Coordonnées",
    summary: "Résumé",
  },
  callback: {
    label: "Quand peut-on vous rappeler ?",
    hint: "Nous vous rappelons systématiquement pour préparer votre devis.",
    options: {
      asap: "Dès que possible",
      morning: "Ce matin",
      afternoon: "Cet après-midi",
      evening: "Ce soir",
      tomorrow: "Demain",
      any: "Peu importe",
    },
  },
  contactMethod: {
    label: "Méthode de contact préférée",
    options: { phone: "Téléphone", whatsapp: "WhatsApp", email: "E-mail" },
  },
  optIn: "J'accepte d'être recontacté par Mopper.",
  optInHint: "Aucune inscription à une newsletter. Uniquement pour ce devis.",
  precise: "Date précise",
  dontKnow: IDK_FR,
  whatsappPhotos: "Envoyer mes photos sur WhatsApp",
  photosCta: "Ajoutez quelques photos si vous en avez",
  photosCtaHint: "Cela nous aide à comprendre plus vite la situation et à préparer un devis plus précis.",
  reassure: "Pas besoin d'être précis à 100 %. Nous vous rappellerons pour clarifier.",
  submitReassure: "Merci ! Nous avons les premières informations nécessaires. Un membre de l'équipe Mopper vous rappellera pour clarifier les détails et préparer votre devis.",
  fieldsRequired: "* Champs indispensables",
  // Enrichments per service
  move: {
    sizeLabel: "Taille du logement",
    sizes: {
      studio: "Studio",
      p2: "2 / 2.5 pièces",
      p3: "3 / 3.5 pièces",
      p4: "4 / 4.5 pièces",
      p5: "5 pièces et +",
      house: "Maison",
      pro: "Bureau / local pro",
      idk: IDK_FR,
    },
    extraLabel: "Options complémentaires (facultatif)",
    extras: {
      boxes: "Cartons",
      packing: "Emballage",
      dis: "Démontage / remontage",
      clean: "Nettoyage après déménagement",
      clear: "Débarras d'objets",
    },
    accessLabel: "Contrainte d'accès",
    access: {
      easy: "Accès facile",
      narrow: "Escaliers / ascenseur étroit",
      parking: "Stationnement difficile",
      idk: IDK_FR,
    },
  },
  clear: {
    spaceLabel: "Type d'espace",
    spaces: { cave: "Cave", grenier: "Grenier", garage: "Garage", appartement: "Appartement", maison: "Maison", pro: "Local professionnel", other: "Autre" },
    volumeLabel: "Volume approximatif",
    volumes: {
      few: "Quelques objets",
      half: "Une demi-pièce",
      one: "Une pièce complète",
      several: "Plusieurs pièces",
      full: "Logement complet",
      idk: IDK_FR,
    },
    situationLabel: "Situation particulière (facultatif)",
    situations: {
      succession: "Succession",
      lease: "Fin de bail",
      ems: "Départ en EMS",
      packed: "Logement très encombré",
      afterMove: "Après déménagement",
      none: "Aucune / autre",
    },
    heavyLabel: "Objets lourds ou spéciaux",
  },
  clean: {
    typeLabel: "Type de nettoyage",
    types: {
      fin_bail: "Fin de bail",
      apres_demenagement: "Après déménagement",
      apres_debarras: "Après débarras",
      avant_emm: "Avant emménagement",
      ponctuel: "Nettoyage ponctuel",
      regulier: "Nettoyage régulier",
      autre: "Autre",
    },
    optionsLabel: "Options (facultatif)",
    options: {
      windows: "Vitres",
      appliances: "Électroménager",
      balcony: "Balcon / terrasse",
      cellar: "Cave / garage",
      handover: "Présence à l'état des lieux",
    },
    stateLabel: "État général",
    states: {
      normal: "Normal",
      dirty: "Très sale",
      empty: "Logement vide",
      furnished: "Logement meublé",
      idk: IDK_FR,
    },
  },
  organize: {
    scopeLabel: "Périmètre",
    scopes: { room: "Une pièce", multi: "Plusieurs pièces", full: "Logement complet", idk: "Je ne sais pas encore" },
    goalLabel: "Objectif principal",
    goals: {
      space: "Gagner de la place",
      practical: "Rendre l'espace plus pratique",
      esthetic: "Rendre l'espace plus esthétique",
      afterMove: "Organiser après un déménagement",
      baby: "Préparer une naissance",
      declutter: "Désencombrer avant d'organiser",
      idk: IDK_FR,
    },
  },
  transport: {
    itemLabel: "Objet à transporter",
    items: {
      sofa: "Canapé", bed: "Lit", wardrobe: "Armoire", table: "Table",
      appliance: "Électroménager", multi: "Plusieurs meubles", other: "Autre",
    },
    weightLabel: "Objet lourd ou fragile ?",
    disLabel: "Démontage / remontage",
    disYes: "Oui", disNo: "Non", disTalk: "À discuter",
  },
  full: {
    title: "Vous voulez déléguer plusieurs choses à Mopper ?",
    sub: "Cochez ce dont vous avez besoin. Nous vous rappellerons pour organiser tout ça avec vous.",
    options: {
      move: "Déménagement", clear: "Débarras", clean: "Nettoyage",
      organize: "Home Organizing", transport: "Transport de meubles",
      assembly: "Montage / démontage", idk: "Je ne sais pas encore",
    },
  },
  yn: { yes: "Oui", no: "Non", idk: IDK_FR },
};

const DE = {
  intro: {
    banner: "Erzählen Sie uns Ihr Anliegen in wenigen Klicks. Sie müssen nicht zu 100 % präzise sein — wir rufen Sie zurück, um die Details zu klären und Ihr Angebot vorzubereiten.",
    duration: "≈ 1 Minute",
    freeNoEng: "Kostenlos und unverbindlich",
  },
  steps: { service: "Dienst", details: "Details", contact: "Kontakt", summary: "Zusammenfassung" },
  callback: {
    label: "Wann dürfen wir Sie zurückrufen?",
    hint: "Wir rufen Sie systematisch zurück, um Ihr Angebot vorzubereiten.",
    options: {
      asap: "So bald wie möglich",
      morning: "Heute Vormittag",
      afternoon: "Heute Nachmittag",
      evening: "Heute Abend",
      tomorrow: "Morgen",
      any: "Egal wann",
    },
  },
  contactMethod: {
    label: "Bevorzugte Kontaktart",
    options: { phone: "Telefon", whatsapp: "WhatsApp", email: "E-Mail" },
  },
  optIn: "Ich stimme zu, von Mopper kontaktiert zu werden.",
  optInHint: "Keine Newsletter-Anmeldung. Nur für dieses Angebot.",
  precise: "Genaues Datum",
  dontKnow: IDK_DE,
  whatsappPhotos: "Fotos per WhatsApp senden",
  photosCta: "Fügen Sie einige Fotos hinzu, falls vorhanden",
  photosCtaHint: "So verstehen wir die Situation schneller und erstellen ein präziseres Angebot.",
  reassure: "Sie müssen nicht zu 100 % präzise sein. Wir rufen Sie zurück, um zu klären.",
  submitReassure: "Danke! Wir haben die ersten notwendigen Informationen. Ein Mitglied des Mopper-Teams wird Sie anrufen, um die Details zu klären und Ihr Angebot vorzubereiten.",
  fieldsRequired: "* Pflichtfelder",
  move: {
    sizeLabel: "Wohnungsgrösse",
    sizes: {
      studio: "Studio", p2: "2 / 2.5 Zi.", p3: "3 / 3.5 Zi.", p4: "4 / 4.5 Zi.",
      p5: "5 Zi. und +", house: "Haus", pro: "Büro / Gewerbe", idk: IDK_DE,
    },
    extraLabel: "Zusatzoptionen (optional)",
    extras: {
      boxes: "Kartons", packing: "Verpacken", dis: "Demontage / Montage",
      clean: "Endreinigung nach Umzug", clear: "Räumung von Gegenständen",
    },
    accessLabel: "Zugangsbeschränkung",
    access: {
      easy: "Einfacher Zugang",
      narrow: "Enge Treppe / Aufzug",
      parking: "Schwieriges Parken",
      idk: IDK_DE,
    },
  },
  clear: {
    spaceLabel: "Raumtyp",
    spaces: { cave: "Keller", grenier: "Dachboden", garage: "Garage", appartement: "Wohnung", maison: "Haus", pro: "Gewerbe", other: "Andere" },
    volumeLabel: "Ungefähres Volumen",
    volumes: {
      few: "Einige Gegenstände", half: "Ein halbes Zimmer", one: "Ein ganzes Zimmer",
      several: "Mehrere Zimmer", full: "Ganze Wohnung", idk: IDK_DE,
    },
    situationLabel: "Besondere Situation (optional)",
    situations: {
      succession: "Erbschaft", lease: "Auszug / Mietende",
      ems: "Umzug ins Pflegeheim", packed: "Sehr überladene Wohnung",
      afterMove: "Nach dem Umzug", none: "Keine / andere",
    },
    heavyLabel: "Schwere oder spezielle Gegenstände",
  },
  clean: {
    typeLabel: "Reinigungsart",
    types: {
      fin_bail: "Endreinigung", apres_demenagement: "Nach Umzug",
      apres_debarras: "Nach Räumung", avant_emm: "Vor Einzug",
      ponctuel: "Einmalige Reinigung", regulier: "Regelmässige Reinigung", autre: "Andere",
    },
    optionsLabel: "Optionen (optional)",
    options: {
      windows: "Fenster", appliances: "Haushaltsgeräte",
      balcony: "Balkon / Terrasse", cellar: "Keller / Garage",
      handover: "Anwesenheit bei Übergabe",
    },
    stateLabel: "Zustand",
    states: {
      normal: "Normal", dirty: "Sehr schmutzig", empty: "Leere Wohnung",
      furnished: "Möblierte Wohnung", idk: IDK_DE,
    },
  },
  organize: {
    scopeLabel: "Umfang",
    scopes: { room: "Ein Zimmer", multi: "Mehrere Zimmer", full: "Ganze Wohnung", idk: "Weiss ich noch nicht" },
    goalLabel: "Hauptziel",
    goals: {
      space: "Platz gewinnen", practical: "Praktischer machen",
      esthetic: "Ästhetischer machen", afterMove: "Nach dem Umzug organisieren",
      baby: "Geburt vorbereiten", declutter: "Vor Organisieren entrümpeln",
      idk: IDK_DE,
    },
  },
  transport: {
    itemLabel: "Zu transportierender Gegenstand",
    items: {
      sofa: "Sofa", bed: "Bett", wardrobe: "Schrank", table: "Tisch",
      appliance: "Haushaltsgerät", multi: "Mehrere Möbel", other: "Andere",
    },
    weightLabel: "Schwerer oder zerbrechlicher Gegenstand?",
    disLabel: "Demontage / Montage",
    disYes: "Ja", disNo: "Nein", disTalk: "Zu besprechen",
  },
  full: {
    title: "Möchten Sie mehrere Dinge an Mopper delegieren?",
    sub: "Wählen Sie aus, was Sie brauchen. Wir rufen Sie zurück, um alles mit Ihnen zu organisieren.",
    options: {
      move: "Umzug", clear: "Räumung", clean: "Reinigung",
      organize: "Home Organizing", transport: "Möbeltransport",
      assembly: "Montage / Demontage", idk: "Weiss ich noch nicht",
    },
  },
  yn: { yes: "Ja", no: "Nein", idk: IDK_DE },
};

const EN = {
  intro: {
    banner: "Tell us about your need in a few clicks. No need to be 100% precise — we will call you back to clarify the details and prepare your quote.",
    duration: "≈ 1 minute",
    freeNoEng: "Free & no commitment",
  },
  steps: { service: "Service", details: "Details", contact: "Contact", summary: "Summary" },
  callback: {
    label: "When can we call you back?",
    hint: "We systematically call you back to prepare your quote.",
    options: {
      asap: "As soon as possible", morning: "This morning",
      afternoon: "This afternoon", evening: "This evening",
      tomorrow: "Tomorrow", any: "Anytime works",
    },
  },
  contactMethod: {
    label: "Preferred contact method",
    options: { phone: "Phone", whatsapp: "WhatsApp", email: "Email" },
  },
  optIn: "I agree to be contacted by Mopper.",
  optInHint: "No newsletter. Only for this quote.",
  precise: "Specific date",
  dontKnow: IDK_EN,
  whatsappPhotos: "Send my photos via WhatsApp",
  photosCta: "Add a few photos if you have some",
  photosCtaHint: "It helps us understand the situation faster and prepare a more accurate quote.",
  reassure: "No need to be 100% precise. We'll call you to clarify.",
  submitReassure: "Thank you! We have the initial information we need. A member of the Mopper team will call you to clarify the details and prepare your quote.",
  fieldsRequired: "* Required fields",
  move: {
    sizeLabel: "Home size",
    sizes: {
      studio: "Studio", p2: "2 / 2.5 rooms", p3: "3 / 3.5 rooms", p4: "4 / 4.5 rooms",
      p5: "5+ rooms", house: "House", pro: "Office / commercial", idk: IDK_EN,
    },
    extraLabel: "Extra services (optional)",
    extras: {
      boxes: "Boxes", packing: "Packing", dis: "Disassembly / reassembly",
      clean: "Post-move cleaning", clear: "Clearing of items",
    },
    accessLabel: "Access constraint",
    access: {
      easy: "Easy access", narrow: "Narrow stairs / elevator",
      parking: "Difficult parking", idk: IDK_EN,
    },
  },
  clear: {
    spaceLabel: "Type of space",
    spaces: { cave: "Cellar", grenier: "Attic", garage: "Garage", appartement: "Apartment", maison: "House", pro: "Commercial", other: "Other" },
    volumeLabel: "Approximate volume",
    volumes: {
      few: "A few items", half: "Half a room", one: "A full room",
      several: "Several rooms", full: "Entire home", idk: IDK_EN,
    },
    situationLabel: "Special situation (optional)",
    situations: {
      succession: "Estate settlement", lease: "End of lease",
      ems: "Move to care home", packed: "Very cluttered home",
      afterMove: "After a move", none: "None / other",
    },
    heavyLabel: "Heavy or special items",
  },
  clean: {
    typeLabel: "Cleaning type",
    types: {
      fin_bail: "End-of-lease", apres_demenagement: "After a move",
      apres_debarras: "After clearing", avant_emm: "Before move-in",
      ponctuel: "One-time cleaning", regulier: "Regular cleaning", autre: "Other",
    },
    optionsLabel: "Options (optional)",
    options: {
      windows: "Windows", appliances: "Appliances",
      balcony: "Balcony / terrace", cellar: "Cellar / garage",
      handover: "Attend the walkthrough",
    },
    stateLabel: "Condition",
    states: {
      normal: "Normal", dirty: "Very dirty", empty: "Empty home",
      furnished: "Furnished home", idk: IDK_EN,
    },
  },
  organize: {
    scopeLabel: "Scope",
    scopes: { room: "One room", multi: "Several rooms", full: "Entire home", idk: "Not sure yet" },
    goalLabel: "Main goal",
    goals: {
      space: "Gain space", practical: "Make it more practical",
      esthetic: "Make it more esthetic", afterMove: "Organize after a move",
      baby: "Prepare for a baby", declutter: "Declutter before organizing",
      idk: IDK_EN,
    },
  },
  transport: {
    itemLabel: "Item to transport",
    items: {
      sofa: "Sofa", bed: "Bed", wardrobe: "Wardrobe", table: "Table",
      appliance: "Appliance", multi: "Multiple items", other: "Other",
    },
    weightLabel: "Heavy or fragile item?",
    disLabel: "Disassembly / reassembly",
    disYes: "Yes", disNo: "No", disTalk: "To discuss",
  },
  full: {
    title: "Want to delegate several things to Mopper?",
    sub: "Check what you need. We'll call you back to organize everything with you.",
    options: {
      move: "Moving", clear: "Clearing", clean: "Cleaning",
      organize: "Home Organizing", transport: "Furniture transport",
      assembly: "Assembly / disassembly", idk: "Not sure yet",
    },
  },
  yn: { yes: "Yes", no: "No", idk: IDK_EN },
};

const DICT = { fr: FR, de: DE, en: EN };

/** Public helpers */
export function ext(lang) { return DICT[lang] || DICT.fr; }
export const WHATSAPP_NUMBER_INTL = "41782321850";
export function whatsappUrl(msg = "Bonjour Mopper, voici mes photos pour ma demande de devis.") {
  const t = encodeURIComponent(msg);
  return `https://wa.me/${WHATSAPP_NUMBER_INTL}?text=${t}`;
}
